// Preferencja wyglądu pól tekstowych (współdzielony komponent components/Input,
// oparty o MUI TextField). Trzymana w localStorage pod kluczem `input_appearance_v1`
// (scope "user"); trwałość/synchronizacja między urządzeniami przez mirror AppSettings
// (utils/appSettingsSync.ts).
//
// Stosowanie (tylko po świadomym wyborze użytkownika → atrybut data-input-custom):
//  - zaokrąglenie, wysokość (padding pionowy), gęstość (padding poziomy + font),
//    odstęp (margines pod polem) → zmienne CSS na :root, konsumowane w global.scss
//    dla pól z klasą .app-input,
//  - styl pola → prop `variant` MUI (filled/outlined/standard) na komponencie Input.
// Domyślne wartości = obecny wygląd, więc bez wyboru nic się nie zmienia.

export type InputRadius = "sharp" | "normal" | "round";
export type InputVariant = "filled" | "outline" | "standard";
export type InputHeight = "low" | "standard" | "tall";
export type InputDensity = "compact" | "standard" | "comfortable";
export type InputGap = "compact" | "standard" | "spacious";

export interface InputAppearance {
  /** Zaokrąglenie narożników. */
  radius: InputRadius;
  /** Wariant: wypełnione (szare tło) vs konturowe (białe tło + ramka). */
  variant: InputVariant;
  /** Wysokość pola (padding pionowy). */
  height: InputHeight;
  /** Gęstość (padding poziomy + rozmiar tekstu). */
  density: InputDensity;
  /** Odstęp pomiędzy polami (margines pod polem). */
  gap: InputGap;
}

export const INPUT_APPEARANCE_KEY = "input_appearance_v1";
export const INPUT_APPEARANCE_EVENT = "input-appearance-changed";

export const DEFAULT_INPUT_APPEARANCE: InputAppearance = {
  radius: "normal",
  variant: "filled",
  height: "standard",
  density: "standard",
  gap: "standard",
};

export const RADIUS_PX: Record<InputRadius, string> = {
  sharp: "2px",
  normal: "4px",
  round: "12px",
};
// Pionowy padding pola (wysokość) — „standard" ≈ natywny MUI.
export const PAD_Y_PX: Record<InputHeight, string> = {
  low: "2px",
  standard: "9px",
  tall: "15px",
};
// Poziomy padding pola (gęstość).
export const PAD_X_PX: Record<InputDensity, string> = {
  compact: "8px",
  standard: "12px",
  comfortable: "18px",
};
// Rozmiar tekstu (gęstość) — „standard" = obecny.
export const FONT_PX: Record<InputDensity, string> = {
  compact: "14px",
  standard: "16px",
  comfortable: "16px",
};
// Odstęp pomiędzy polami (margines pod polem) — „standard" ≈ obecny rytm formularzy.
export const GAP_PX: Record<InputGap, string> = {
  compact: "10px",
  standard: "14px",
  spacious: "24px",
};

export const readInputAppearance = (): InputAppearance => {
  try {
    const raw = window.localStorage.getItem(INPUT_APPEARANCE_KEY);
    if (!raw) return DEFAULT_INPUT_APPEARANCE;
    const parsed = JSON.parse(raw) as Partial<InputAppearance>;
    return {
      radius: parsed.radius ?? DEFAULT_INPUT_APPEARANCE.radius,
      variant: parsed.variant ?? DEFAULT_INPUT_APPEARANCE.variant,
      height: parsed.height ?? DEFAULT_INPUT_APPEARANCE.height,
      density: parsed.density ?? DEFAULT_INPUT_APPEARANCE.density,
      gap: parsed.gap ?? DEFAULT_INPUT_APPEARANCE.gap,
    };
  } catch {
    return DEFAULT_INPUT_APPEARANCE;
  }
};

// Mapowanie stylu pola (preferencja) na wariant MUI TextField.
export const muiVariant = (
  v: InputVariant,
): "filled" | "outlined" | "standard" => {
  if (v === "outline") return "outlined";
  if (v === "standard") return "standard";
  return "filled";
};

// Bieżąca preferencja w cache (bez odczytu localStorage per-render w komponencie Input).
let currentInputAppearance: InputAppearance = DEFAULT_INPUT_APPEARANCE;
export const getCurrentInputAppearance = (): InputAppearance =>
  currentInputAppearance;

// Ustawia zmienne CSS na :root i odświeża cache.
// `explicit` = użytkownik świadomie zapisał preferencję → dopiero wtedy włączamy
// nadpisania (atrybut data-input-custom). Bez tego pola wyglądają dokładnie jak dotąd.
export const applyInputAppearance = (
  view: InputAppearance,
  explicit: boolean,
): void => {
  currentInputAppearance = view;
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  root.style.setProperty("--app-input-radius", RADIUS_PX[view.radius]);
  root.style.setProperty("--app-input-pad-y", PAD_Y_PX[view.height]);
  root.style.setProperty("--app-input-pad-x", PAD_X_PX[view.density]);
  root.style.setProperty("--app-input-font", FONT_PX[view.density]);
  root.style.setProperty("--app-input-gap", GAP_PX[view.gap]);
  if (explicit) {
    root.setAttribute("data-input-custom", "1");
  } else {
    root.removeAttribute("data-input-custom");
  }
};

export const writeInputAppearance = (view: InputAppearance): void => {
  try {
    window.localStorage.setItem(INPUT_APPEARANCE_KEY, JSON.stringify(view));
    applyInputAppearance(view, true);
    window.dispatchEvent(new CustomEvent(INPUT_APPEARANCE_EVENT));
  } catch {
    /* brak window / quota — pomijamy */
  }
};

// Instalacja globalna (raz przy starcie): aplikuje zapisaną preferencję i reaguje na
// zmiany (ta karta, inna karta, hydratacja AppSettings z BE).
export const installInputAppearance = (): void => {
  if (typeof window === "undefined") return;
  const refresh = () => {
    const saved = window.localStorage.getItem(INPUT_APPEARANCE_KEY);
    applyInputAppearance(readInputAppearance(), !!saved);
  };
  refresh();
  window.addEventListener(INPUT_APPEARANCE_EVENT, refresh);
  window.addEventListener("storage", (e) => {
    if (e.key === INPUT_APPEARANCE_KEY) refresh();
  });
  window.addEventListener("app-settings-hydrated", (e) => {
    const keys = (e as CustomEvent<{ keys?: string[] }>).detail?.keys;
    if (!keys || keys.includes(INPUT_APPEARANCE_KEY)) refresh();
  });
};
